/**
 * 响应式断点常量定义
 * Feature: mobile-responsive-framework, Task 1.1: 实现断点配置和工具函数
 * 需求: 9.1
 */

// 断点配置接口
export interface BreakpointConfig {
  xs: number;   // 超小屏幕 (手机竖屏)
  sm: number;   // 小屏幕 (手机横屏)
  md: number;   // 中等屏幕 (平板竖屏)
  lg: number;   // 大屏幕 (平板横屏/小桌面)
  xl: number;   // 超大屏幕 (桌面)
  xxl: number;  // 超超大屏幕 (大桌面)
}

// 断点名称类型
export type BreakpointName = keyof BreakpointConfig;

// 断点值定义 (与Ant Design保持一致)
export const BREAKPOINTS: BreakpointConfig = {
  xs: 0,      // 0 - 575px
  sm: 576,    // 576 - 767px
  md: 768,    // 768 - 991px
  lg: 992,    // 992 - 1199px
  xl: 1200,   // 1200 - 1599px
  xxl: 1600   // 1600px+
} as const;

// 断点最大值 (用于媒体查询)
export const BREAKPOINT_MAX: BreakpointConfig = {
  xs: 575,
  sm: 767,
  md: 991,
  lg: 1199,
  xl: 1599,
  xxl: Infinity
} as const;

// 设备类型定义
export type DeviceType = 'mobile' | 'tablet' | 'desktop';

// 屏幕方向
export type ScreenOrientation = 'portrait' | 'landscape';

// 设备类型断点映射
export const DEVICE_BREAKPOINTS: Record<DeviceType, { min: number; max: number }> = {
  mobile: { min: 0, max: 575 },
  tablet: { min: 576, max: 991 },
  desktop: { min: 992, max: Infinity }
} as const;

/**
 * 根据屏幕宽度获取当前断点名称
 * @param width 屏幕宽度
 * @returns 断点名称
 */
export function getBreakpointName(width: number): BreakpointName {
  if (width >= BREAKPOINTS.xxl) return 'xxl';
  if (width >= BREAKPOINTS.xl) return 'xl';
  if (width >= BREAKPOINTS.lg) return 'lg';
  if (width >= BREAKPOINTS.md) return 'md';
  if (width >= BREAKPOINTS.sm) return 'sm';
  return 'xs';
}

/**
 * 根据屏幕宽度获取设备类型
 * @param width 屏幕宽度
 * @returns 设备类型
 */
export function getDeviceType(width: number): DeviceType {
  if (width <= DEVICE_BREAKPOINTS.mobile.max) return 'mobile';
  if (width <= DEVICE_BREAKPOINTS.tablet.max) return 'tablet';
  return 'desktop';
}

/**
 * 检查当前宽度是否匹配指定断点
 * @param width 屏幕宽度
 * @param breakpoint 断点名称
 * @returns 是否匹配
 */
export function matchesBreakpoint(width: number, breakpoint: BreakpointName): boolean {
  const breakpointValue = BREAKPOINTS[breakpoint];
  const nextBreakpoint = getNextBreakpoint(breakpoint);
  const maxValue = nextBreakpoint ? BREAKPOINTS[nextBreakpoint] - 1 : Infinity;
  
  return width >= breakpointValue && width <= maxValue;
}

/**
 * 获取下一个断点名称
 * @param breakpoint 当前断点
 * @returns 下一个断点名称或undefined
 */
export function getNextBreakpoint(breakpoint: BreakpointName): BreakpointName | undefined {
  const breakpointOrder: BreakpointName[] = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'];
  const currentIndex = breakpointOrder.indexOf(breakpoint);
  return currentIndex < breakpointOrder.length - 1 ? breakpointOrder[currentIndex + 1] : undefined;
}

/**
 * 生成媒体查询字符串
 * @param minBreakpoint 最小断点
 * @param maxBreakpoint 最大断点（可选）
 * @returns 媒体查询字符串
 */
export function generateMediaQuery(
  minBreakpoint?: BreakpointName,
  maxBreakpoint?: BreakpointName
): string {
  const conditions: string[] = [];
  
  if (minBreakpoint) {
    conditions.push(`(min-width: ${BREAKPOINTS[minBreakpoint]}px)`);
  }
  
  if (maxBreakpoint) {
    conditions.push(`(max-width: ${BREAKPOINT_MAX[maxBreakpoint]}px)`);
  }
  
  return conditions.length > 0 ? conditions.join(' and ') : '';
}

/**
 * 检查是否为移动设备
 * @param width 屏幕宽度
 * @returns 是否为移动设备
 */
export function isMobileWidth(width: number): boolean {
  return width < BREAKPOINTS.sm;
}

/**
 * 检查是否为平板设备
 * @param width 屏幕宽度
 * @returns 是否为平板设备
 */
export function isTabletWidth(width: number): boolean {
  return width >= BREAKPOINTS.sm && width < BREAKPOINTS.lg;
}

/**
 * 检查是否为桌面设备
 * @param width 屏幕宽度
 * @returns 是否为桌面设备
 */
export function isDesktopWidth(width: number): boolean {
  return width >= BREAKPOINTS.lg;
}
